'use client';

// useLotMaxBids / useLotPreBids — the clerk console centre panel's ceiling source
// (BL-1552). The max-bid sibling of `useAdminLotBidLedger` (BUG-1467), and the
// replacement for `useMaxBids` / `usePreBids`, which folded an AUCTION-WIDE feed
// and filtered it to a lot on the client.
//
// WHY THEY EXIST. The admin max-bid catch-up was auction-wide and uncapped: at
// 2,500 lots its cold seed was 5.7 MB — the single largest payload in the
// console's ~8.3 MB load, and growing with the sale — to answer a question about
// ONE lot. Scoping the subscription to the selected lot is what makes the
// console's load flat in auction size (ADR 053 — Clerk Console Scale § 4).
//
// HOOKS, NOT A PROVIDER, and that is deliberate. The auction-wide feed was a
// page-level provider because every consumer wanted the same one auction. This
// feed is per SELECTED LOT: it attaches when a clerk selects a lot and lets go
// when they select another, so it belongs to the component that renders the
// selection. The only ancestor is the generic `MaxBidsProvider`, which is not
// auction-keyed — that is what lets a bid-record unit mount outside the console
// (the 2026-09-04 addendum; BL-1542's precondition).
//
// RENAMED, NOT REPOINTED. `useMaxBids()` / `usePreBids()` took an OPTIONAL
// `lotId` that merely filtered — omitting it gave the whole auction. Keeping
// those names over a per-lot subscription would leave a caller able to write
// `useMaxBids()` and silently get one lot's rows instead of the auction's. The
// lot id is now the subscription key, so it is positional and required
// (ADR 044 — Must Consolidate: the old spelling does not survive with new
// semantics).

import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

import {
    decodeAdminMaxBidDelta,
    type AdminMaxBidDelta,
    type AdminMaxBidDeltaDecoded,
} from '../../mappers/realtime/admin-max-bids';
import { formatRealtimeFailure } from '../../isomorphic/realtime/realtime-failure-log';
import { selectLotPreBids } from '../../isomorphic/clerk-console/selectLotPreBids';

import type { EntityStateStoreRegistry } from '../../stores/realtime/entity-state-registry';
import { useMaxBidRegistry } from '../../components/realtime/MaxBidsProvider';
import type { MaxBidStateStore } from '../../stores/realtime/max-bid-state-store';

/** Stable "feed is fine" value so a no-lot render keeps a stable snapshot. */
const AVAILABLE_FEED_STATE: LotMaxBidsFeedState = { unavailable: false };
const UNAVAILABLE_FEED_STATE: LotMaxBidsFeedState = { unavailable: true };

/** Stable empty map for the no-lot-selected and SSR snapshots (ref-stable). */
const EMPTY_MAX_BIDS: ReadonlyMap<string, AdminMaxBidDelta> = new Map();

/** Stable empty array so a no-lot render doesn't churn consumers' memo deps. */
const EMPTY_DELTAS: AdminMaxBidDeltaDecoded[] = [];

/**
 * Take a ref-counted reference to `lotId`'s store for the caller's lifetime.
 *
 * Every hook that READS the store must also hold it. Peeking without acquiring
 * works only for as long as some sibling hook happens to acquire the same lot in
 * the same tree — a standalone consumer would peek an absent store and read its
 * "nothing is wrong" default forever, which is precisely the silent-wrong-answer
 * this module exists to prevent.
 *
 * A second acquire on the same lot is free: the registry ref-counts, so both
 * hooks share ONE store and one wire subscription (RAC 6).
 *
 * `lotId` in the deps is load-bearing: without it a clerk who selects a second
 * lot stays subscribed to the first, and the leak is invisible because the new
 * lot's rows arrive too — it just also holds every previously-selected lot's
 * feed open for the length of the sale.
 */
function useHoldLotMaxBidStore(
    registry: EntityStateStoreRegistry<MaxBidStateStore>,
    lotId: string | null,
): void {
    useEffect(() => {
        if (lotId == null) return;
        registry.acquire(lotId);
        return () => registry.release(lotId);
    }, [registry, lotId]);
}

/**
 * Hold a ref-counted subscription to `lotId`'s max-bid store and read its fold.
 *
 * The store is shared: two components on the same lot get one wire subscription,
 * and unmounting one does not tear down the other (RAC 6) — the registry owns
 * that.
 */
function useLotMaxBidMap(lotId: string | null): ReadonlyMap<string, AdminMaxBidDelta> {
    const registry = useMaxBidRegistry();

    useHoldLotMaxBidStore(registry, lotId);

    // Read through the store WITHOUT taking a reference — `acquire` above owns
    // the lifecycle. During the render before the effect runs, the store may not
    // exist yet; `peek` returns undefined and the caller renders empty.
    const subscribe = useCallback(
        (listener: () => void) => {
            if (lotId == null) return () => {};
            return registry.peek(lotId)?.subscribe(listener) ?? (() => {});
        },
        [registry, lotId],
    );
    const getSnapshot = useCallback((): ReadonlyMap<string, AdminMaxBidDelta> => {
        if (lotId == null) return EMPTY_MAX_BIDS;
        return registry.peek(lotId)?.getMaxBids() ?? EMPTY_MAX_BIDS;
    }, [registry, lotId]);

    return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_MAX_BIDS);
}

/**
 * One lot's `max_bids` rows, folded live — UNFILTERED by status and by
 * `placedDuringPreBid`.
 *
 * This is the raw stream. It is deliberately NOT {@link useLotPreBids}: that hook
 * gates on `placedDuringPreBid`, which is the Pre-bids SECTION's membership rule
 * and would drop every ceiling placed after the auction opened — i.e. most of the
 * ceilings a clerk needs mid-sale. Statuses are unfiltered too, because the
 * ladder's precedence needs `superseded` (to report the replaced figure) and needs
 * to see `cancelled` in order to exclude it.
 *
 * Pair with `selectStandingMaxLadder` to resolve one ceiling per bidder.
 *
 * NOTE these carry no bidder name or paddle — the payload is deliberately
 * identifiers + facts (see `payloads/admin-max-bids.ts`). Join identity on
 * `customerId`.
 *
 * @param lotId - the selected lot. `null` means "nothing selected" — no
 *   subscription, no catch-up, and an empty list.
 * @returns the lot's deltas with each ceiling decoded into a `Money`, in
 *   map-iteration order (the caller sorts).
 * @sideEffect Issues ONE `GET /api/realtime/admin_lot_max_bids/catchup` per
 *   selected lot to learn the obscured topic, then subscribes to it — both shared
 *   with any other consumer of the same lot.
 */
export function useLotMaxBids(lotId: string | null): AdminMaxBidDeltaDecoded[] {
    return useDecodedLotMaxBids(lotId);
}

/**
 * The lot's deltas with each ceiling decoded into a `Money` once, here, at the
 * hook boundary (BL-2010, ADR 060 — Money Type). The store keeps the wire shape;
 * a row whose ceiling is not a well-formed MoneyWire is left out rather than
 * crashing the record, and logged once per `maxBidId` from an effect (a memo
 * must stay pure).
 *
 * @param lotId - the selected lot; `null` is inert.
 * @returns the decoded deltas, in map-iteration order.
 */
function useDecodedLotMaxBids(lotId: string | null): AdminMaxBidDeltaDecoded[] {
    const map = useLotMaxBidMap(lotId);
    const { decoded, undecodable } = useMemo(() => {
        if (map.size === 0) return { decoded: EMPTY_DELTAS, undecodable: [] as AdminMaxBidDelta[] };
        const out: AdminMaxBidDeltaDecoded[] = [];
        const bad: AdminMaxBidDelta[] = [];
        for (const wire of map.values()) {
            const result = decodeAdminMaxBidDelta(wire);
            if (result.kind === 'ok') out.push(result.delta);
            else bad.push(wire);
        }
        return { decoded: out, undecodable: bad };
    }, [map]);

    const logged = useRef(new Set<string>());
    useEffect(() => {
        for (const wire of undecodable) {
            if (logged.current.has(wire.maxBidId)) continue;
            logged.current.add(wire.maxBidId);
            // eslint-disable-next-line no-console
            console.error(
                ...formatRealtimeFailure(
                    '[useLotMaxBids] undecodable max-bid ceiling — row skipped',
                    { lotId, maxBidId: wire.maxBidId },
                    new Error(`unrecognised maxAmount shape: ${JSON.stringify(wire.maxAmount)}`),
                ),
            );
        }
    }, [undecodable, lotId]);

    return decoded;
}

export interface UseLotPreBidsOptions {
    /**
     * Include pre-bids the clerk has cancelled. Default `false` — a cancelled
     * pre-bid is a real delta the stream must deliver (that IS the delete), but a
     * list of standing maximums shouldn't render it. Pass `true` for a history
     * view such as the bid record.
     */
    includeCancelled?: boolean;
    /** Sort comparator over the raw deltas. Defaults to newest-placed first. */
    sortBy?: (a: AdminMaxBidDeltaDecoded, b: AdminMaxBidDeltaDecoded) => number;
}

/**
 * One lot's pre-bids, folded live. The consumer holds NO subscription / fold /
 * catch-up / transport logic.
 *
 * Membership is `placedDuringPreBid`, ordering is newest-first — both owned by
 * `selectLotPreBids` (ADR 010 — Isomorphic Services), not restated here. Scoping
 * the subscription to one lot does not change that rule, which is what BL-1552
 * RAC 2 asserts.
 *
 * @param lotId - the selected lot; `null` is inert.
 * @param opts.includeCancelled - keep cancelled rows (default: drop them).
 * @param opts.sortBy - override the default newest-first ordering.
 * @returns the matching pre-bid deltas, sorted.
 * @sideEffect Shares {@link useLotMaxBids}' subscription for the same lot.
 */
export function useLotPreBids(
    lotId: string | null,
    opts: UseLotPreBidsOptions = {},
): AdminMaxBidDeltaDecoded[] {
    const deltas = useDecodedLotMaxBids(lotId);
    const { includeCancelled = false, sortBy } = opts;
    // Pure delegation — the membership + ordering rule lives in ONE place.
    return useMemo(
        () =>
            selectLotPreBids(deltas, {
                lotId: lotId ?? undefined,
                includeCancelled,
                sortBy,
            }),
        [deltas, lotId, includeCancelled, sortBy],
    );
}

export interface LotMaxBidsFeedState {
    /**
     * TRUE when the last catch-up for this lot FAILED — i.e. an empty ceiling
     * list means "could not read", not "none".
     *
     * Deliberately a boolean and not the error text: the message is a transport
     * detail (`catch-up 500`) that has no business on a clerk's screen, and the
     * copy belongs to the surface rendering it.
     */
    unavailable: boolean;
}

/**
 * Whether this lot's ceilings could be READ — the companion to
 * {@link useLotMaxBids}, which cannot answer it.
 *
 * WHY A SECOND HOOK. A row count is ambiguous: zero is both the right answer for
 * a lot nobody has placed a ceiling on and the symptom of a catch-up that
 * failed. Rendering the second as the first is exactly BUG-1467 — a confident,
 * legitimate-looking empty state that is simply wrong, on a pane a clerk reads
 * back to a buyer. That bug was fixed for the bid ledger; this closes the same
 * hole for ceilings.
 *
 * It is NOT sticky. A feed that recovers clears the flag, so an operator is
 * never stranded on a banner for a stream that is working again.
 *
 * @param lotId - the selected lot; `null` reports available (nothing to read).
 * @returns `{ unavailable }` — see {@link LotMaxBidsFeedState}.
 */
export function useLotMaxBidsFeedState(lotId: string | null): LotMaxBidsFeedState {
    const registry = useMaxBidRegistry();
    // Holds its OWN reference so the hook is correct standalone — see
    // `useHoldLotMaxBidStore`. Sharing a lot with `useLotMaxBids` costs nothing.
    useHoldLotMaxBidStore(registry, lotId);
    const subscribe = useCallback(
        (listener: () => void) => {
            if (lotId == null) return () => {};
            return registry.peek(lotId)?.subscribe(listener) ?? (() => {});
        },
        [registry, lotId],
    );
    // A boolean derived per call would be a new object each time and tear
    // `useSyncExternalStore`; the two frozen constants keep the snapshot stable.
    const getSnapshot = useCallback((): LotMaxBidsFeedState => {
        if (lotId == null) return AVAILABLE_FEED_STATE;
        const conn = registry.peek(lotId)?.getConnection();
        return conn?.lastError != null ? UNAVAILABLE_FEED_STATE : AVAILABLE_FEED_STATE;
    }, [registry, lotId]);

    return useSyncExternalStore(subscribe, getSnapshot, () => AVAILABLE_FEED_STATE);
}

/** Re-exported for the hooks' own tests and for typing a store reference. */
export type { MaxBidStateStore };
